import { useState } from 'react';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const data = new URLSearchParams();
    Array.from(form.elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name) data.append(input.name, input.value);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6mhrktv117fnkj2h5v0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-20 md:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-14 md:mb-16">
          <span className="text-teal-600 text-sm font-semibold tracking-wider uppercase">GET IN TOUCH</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mt-4 px-4">Contact Us</h2>
          <p className="text-slate-500 mt-3 sm:mt-4 text-base sm:text-lg max-w-xl mx-auto px-4">Have a project in mind? Fill out the form below and we'll get back to you within 24 hours.</p>
        </div>

        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto mb-12 sm:mb-14 md:mb-16">
          <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-5 sm:mb-6">
              <i className="ri-phone-line text-teal-600 text-xl sm:text-2xl"></i>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3">Phone</h3>
            <a href="tel:586-443-0294" className="text-slate-600 hover:text-teal-600 transition-colors text-base sm:text-lg font-semibold cursor-pointer">
              586-443-0294
            </a>
            <p className="text-slate-500 text-xs sm:text-sm mt-2">Mon - Fri: 8am - 6pm</p>
          </div>

          <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-5 sm:mb-6">
              <i className="ri-mail-line text-teal-600 text-xl sm:text-2xl"></i>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3">Email</h3>
            <a href="mailto:info@americandreamremodelinc.com" className="text-slate-600 hover:text-teal-600 transition-colors text-sm sm:text-base cursor-pointer break-all px-2">
              info@americandreamremodelinc.com
            </a>
            <p className="text-slate-500 text-xs sm:text-sm mt-2">We reply within 24 hours</p>
          </div>

          <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-5 sm:mb-6">
              <i className="ri-map-pin-line text-teal-600 text-xl sm:text-2xl"></i>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3">Service Area</h3>
            <p className="text-slate-600 text-sm sm:text-base">Metro Detroit</p>
            <p className="text-slate-500 text-xs sm:text-sm mt-2">4 Counties Covered</p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-3xl mx-auto mb-16 sm:mb-18 md:mb-20">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-10">
            {submitted ? (
              <div className="text-center py-10 sm:py-12">
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-5 sm:mb-6">
                  <i className="ri-check-line text-teal-600 text-3xl sm:text-4xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3 px-4">Message Sent!</h3>
                <p className="text-slate-500 text-sm sm:text-base px-4">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 sm:mt-8 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="John Smith"
                      className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(586) 000-0000"
                      className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Service Needed</label>
                    <select
                      name="service"
                      className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option value="Kitchen Remodeling">Kitchen Remodeling</option>
                      <option value="Bathroom Remodeling">Bathroom Remodeling</option>
                      <option value="Basement Finishing">Basement Finishing</option>
                      <option value="Room Additions">Room Additions</option>
                      <option value="Flooring">Flooring</option>
                      <option value="Roofing">Roofing</option>
                      <option value="Windows & Doors">Windows &amp; Doors</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Message <span className="text-red-500">*</span></label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your project — what you're looking to do, your timeline, and any details that would help us assist you..."
                    className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition resize-none"
                  ></textarea>
                  <p className="text-xs text-slate-400 mt-1 text-right">Max 500 characters</p>
                </div>

                {error && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-3 text-sm">
                    <i className="ri-error-warning-line text-base"></i>
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-bold py-3 sm:py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-lg"></i>
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}